import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import Toastify, { notifyError, notifySuccess } from "../../utils/tostfy";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { getApiErrorMessage, handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import EyeIcon from "../../assets/image/eye-icon.svg";
import CancelIcon from "../../assets/image/cross-sm.svg";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import FilterIcon from "../../assets/image/filter.svg";
import DateRangeFilter from "../../Components/DateRangeFilter/DateRangeFilter";
import DateRangeAppliedLabel from "../../Components/DateRangeFilter/DateRangeAppliedLabel";
import { appendDateRangeParams, DATE_PARAM_KEYS } from "../../utils/dateRange";
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../utils/search";
import {
  limitTextInput,
  validateWordCount,
  WORD_COUNT_HINT,
} from "../../utils/textWordValidation";
import { formatDateTime } from "../../utils/formatDateTime";
import { formatTripAmount } from "../../utils/formatAmount";
import { useListPersistence } from "../../hooks/useListPersistence";
import PermissionGate from "../../Components/PermissionGate/PermissionGate";
import { MODULES } from "../../utils/permissions";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";
import TripMeterStatusBadge from "../../Components/TripMeterStatusBadge";
import { isTripMeterStopped } from "../../utils/meterSensorIssue";

const STATUS_OPTIONS = [
  { key: "", label: "All Types", status: null },
  { key: "pending", label: "Pending", status: "0" },
  { key: "ongoing", label: "Ongoing", status: "1" },
  { key: "locked", label: "Locked", status: "2" },
  { key: "completed", label: "Completed", status: "3" },
  { key: "cancelled", label: "Cancelled", status: "4" },
];

const isValidTripHistoryStatus = (key) =>
  STATUS_OPTIONS.some((item) => item.key === key);

const tripStatusLabel = (t) => {
  if (typeof t?.trip_status === "string" && t.trip_status.trim()) return t.trip_status;
  const s = t?.status;
  if (s === 0) return "Pending";
  if (s === 1) return "Ongoing";
  if (s === 2) return "Locked";
  if (s === 3) return "Completed";
  if (s === 4) return "Cancelled";
  if (s != null && s !== "") return String(s);
  return "—";
};

const FilterChevron = ({ open }) => (
  <span
    className={`inline-block text-gray-400 transition-transform duration-200 ${open ? "-rotate-90" : ""}`}
    aria-hidden
  >
    ›
  </span>
);

const trimText = (value, max = 20) => {
  if (value == null) return "—";
  const str = String(value);
  if (str.length <= max) return str;
  return `${str.slice(0, max).trimEnd()}...`;
};

const parseTripAmountLimit = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const getAwardAmountValue = (value) => {
  if (value == null || String(value).trim() === "") return 0;
  return Number(value);
};

const AWARD_AMOUNT_LIMIT = 99999;
const DEBIT_NOTE_AMOUNT_MAX = 9999;

const normalizeDebitNoteAmountInput = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "").slice(0, 4);
  if (!digits) return "0";
  return String(Number(digits));
};

const getDebitNoteAmountValue = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return 0;
  const amount = Number(digits);
  if (!Number.isFinite(amount) || amount < 0) return 0;
  return Math.min(amount, DEBIT_NOTE_AMOUNT_MAX);
};

const normalizeAwardAmountInput = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (raw === "-") return raw;

  let next = raw
    .replace(/[^\d.-]/g, "")
    .replace(/(?!^)-/g, "");

  const dotIdx = next.indexOf(".");
  if (dotIdx !== -1) {
    next =
      next.slice(0, dotIdx + 1) +
      next
        .slice(dotIdx + 1)
        .replace(/\./g, "");
  }

  const amount = Number(next);
  if (!Number.isFinite(amount)) return next;
  if (amount > AWARD_AMOUNT_LIMIT) return String(AWARD_AMOUNT_LIMIT);
  if (amount < -AWARD_AMOUNT_LIMIT) return String(-AWARD_AMOUNT_LIMIT);
  return next;
};

const clampTripAmountValue = (value, maxTripAmount) => {
  if (value == null || value === "") return value;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value;
  return maxTripAmount != null && amount > maxTripAmount ? String(maxTripAmount) : value;
};

const getCancelTripResponseMessage = (response, fallback = "") => {
  const data = response?.response?.data ?? response?.data;
  const message =
    data?.errorMessage ??
    data?.message ??
    data?.msg ??
    response?.errorMessage ??
    response?.message;
  return typeof message === "string" && message.trim() ? message.trim() : fallback;
};

const isCancelTripSuccessResponse = (response) => {
  const data = response?.data;
  const code = Number(data?.code);
  return (
    response?.status >= 200 &&
    response?.status < 300 &&
    ([200, 201].includes(code) || data?.success === true)
  );
};

const resolveCancelTypeLabel = (t) => {
  const ct = t?.trip_cancel_type_id;
  if (typeof ct === "object" && ct != null) {
    return ct.type ?? ct.name ?? ct.cancel_type ?? "—";
  }
  return ct != null && String(ct).trim() !== "" ? String(ct) : "—";
};

const resolveCancelledByLabel = (t) => {
  const by = t?.trip_cancelled_by_id;
  if (typeof by === "object" && by != null) {
    const name = by.name ?? by.full_name ?? by.accountant_name ?? by.username ?? by.email ?? "—";
    const employeeId = by.employee_id ?? by.employeeId ?? by.user_ref_id ?? by.userRefId ?? "";
    return employeeId && name !== "—" ? `${name} (${employeeId})` : name;
  }
  return by != null && String(by).trim() !== "" ? String(by) : "—";
};

const mapTripListingRow = (t) => {
  const company = t?.company_id;
  const companyLoc =
    (typeof company === "object" && company?.company_location_id?.name) ||
    t?.company_location_id?.name ||
    t?.company_location?.name ||
    "—";
  const vid = t?.vehicle_id;
  const vehicleLabel =
    typeof vid === "object" && vid != null
      ? vid.registration_number || vid.name || "—"
      : t?.vehicle?.registration_number ?? t?.vehicle_number ?? "—";
  const challan =
    t?.challan_number != null && String(t.challan_number).trim() !== ""
      ? String(t.challan_number)
      : "—";

  return {
    id: t?._id ?? t?.id ?? "",
    company:
      (typeof company === "object" && company?.name) ||
      t?.company?.name ||
      t?.company_name ||
      "—",
    location: companyLoc,
    miplLocation:
      t?.mipl_location_id?.name ?? t?.mipl_location?.name ?? t?.mipl_location_name ?? "—",
    vehicle: vehicleLabel,
    tripRefId:
      t?.trip_ref_id != null && String(t.trip_ref_id).trim() !== ""
        ? String(t.trip_ref_id)
        : "—",
    challanNumber: challan,
    status: tripStatusLabel(t),
    cancelledAt: formatDateTime(t?.trip_cancelled_at),
    cancelType: resolveCancelTypeLabel(t),
    cancelTripAmount: formatTripAmount(t?.trip_cancel_trip_amount),
    awardAmount: formatTripAmount(t?.trip_cancel_trip_award_amount),
    cancelReason:
      t?.trip_cancel_reason != null && String(t.trip_cancel_reason).trim() !== ""
        ? String(t.trip_cancel_reason)
        : "—",
    cancelledBy: resolveCancelledByLabel(t),
    maxTripAmount: parseTripAmountLimit(t?.single_driver_trip_amount),
    maxAwardAmount: parseTripAmountLimit(t?.award_amount),
    meterStopped: isTripMeterStopped(t),
  };
};

const extractCancelTypeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.cancel_type_data)) return data.cancel_type_data;
  if (Array.isArray(data?.trip_cancel_type_list)) return data.trip_cancel_type_list;
  if (Array.isArray(data?.cancel_type_list)) return data.cancel_type_list;
  return [];
};

const mapCancelTypeOption = (item) => {
  const label = item?.type ?? item?.name ?? item?.cancel_type ?? "—";
  const normalized = String(label).toLowerCase().trim();
  let variant = "other";
  if (normalized.includes("down")) variant = "down";
  else if (normalized.includes("technical")) variant = "technical";
  else if (normalized.includes("up")) variant = "up";

  return {
    id: item?._id ?? item?.id ?? "",
    label,
    variant,
  };
};

const amountFieldClassName =
  "h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary";

const TripHistoryListing = () => {
  const LIST_PATH = "/trip-history";
  const { setSubheading } = useOutletContext();
  const filterRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [statusFilter, setStatusFilter] = useState(() => {
    const urlStatus = searchParams.get("status") ?? searchParams.get("tab") ?? "";
    return isValidTripHistoryStatus(urlStatus) ? urlStatus : "";
  });
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    clampSearchInput(searchParams.get("search") || "").trim()
  );
  const [currentPage, setCurrentPage] = useState(() => {
    const p = Number(searchParams.get("page"));
    return Number.isFinite(p) && p > 0 ? p : 1;
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const l = Number(searchParams.get("limit"));
    return Number.isFinite(l) && l > 0 ? l : 10;
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [filterOpen, setFilterOpen] = useState(false);
  const [filterSub, setFilterSub] = useState(null);
  const [miplLocationId, setMiplLocationId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [appliedMiplLocationId, setAppliedMiplLocationId] = useState(
    () => searchParams.get("mipl_location_id") || ""
  );
  const [appliedDateFrom, setAppliedDateFrom] = useState(
    () => searchParams.get("from_date") || ""
  );
  const [appliedDateTo, setAppliedDateTo] = useState(
    () => searchParams.get("to_date") || ""
  );

  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelTrip, setCancelTrip] = useState(null);
  const [cancelTypes, setCancelTypes] = useState([]);
  const [cancelTypesLoading, setCancelTypesLoading] = useState(false);
  const [selectedCancelTypeId, setSelectedCancelTypeId] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [tripAmount, setTripAmount] = useState("");
  const [awardAmount, setAwardAmount] = useState("");
  const [debitAmount, setDebitAmount] = useState("0");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelFieldError, setCancelFieldError] = useState({ field: "", message: "" });
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const applyRestoredState = useCallback((saved) => {
    if (saved.search != null) {
      setSearch(saved.search);
      setDebouncedSearch(clampSearchInput(saved.search).trim());
    }
    if (saved.page) {
      const p = Number(saved.page);
      if (Number.isFinite(p) && p > 0) setCurrentPage(p);
    }
    if (saved.limit) {
      const l = Number(saved.limit);
      if (Number.isFinite(l) && l > 0) setItemsPerPage(l);
    }
    if (saved.mipl_location_id) {
      setAppliedMiplLocationId(saved.mipl_location_id);
      setMiplLocationId(saved.mipl_location_id);
    }
    if (saved.from_date) {
      setAppliedDateFrom(saved.from_date);
      setDateFrom(saved.from_date);
    }
    if (saved.to_date) {
      setAppliedDateTo(saved.to_date);
      setDateTo(saved.to_date);
    }
    const status = saved.status ?? saved.tab;
    if (status !== undefined && isValidTripHistoryStatus(status)) {
      setStatusFilter(status);
    }
  }, []);

  const { skipPageResetRef, detailListQuery } = useListPersistence(
    LIST_PATH,
    {
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearch,
      status: statusFilter || undefined,
      mipl_location_id: appliedMiplLocationId || undefined,
      from_date: appliedDateFrom || undefined,
      to_date: appliedDateTo || undefined,
    },
    applyRestoredState
  );

  const selectedCancelType = useMemo(
    () => cancelTypes.find((item) => item.id === selectedCancelTypeId) ?? null,
    [cancelTypes, selectedCancelTypeId]
  );

  useEffect(() => {
    setSubheading("Trip History");
    return () => setSubheading("");
  }, [setSubheading]);

  useEffect(() => {
    const urlStatus = searchParams.get("status") ?? searchParams.get("tab") ?? "";
    const nextStatus = isValidTripHistoryStatus(urlStatus) ? urlStatus : "";
    setStatusFilter((current) => (current === nextStatus ? current : nextStatus));
  }, [searchParams]);

  const handleStatusFilterChange = (key) => {
    setStatusFilter(key);
    setCurrentPage(1);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(clampSearchInput(search).trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useLayoutEffect(() => {
    if (skipPageResetRef.current) return;
    setCurrentPage(1);
  }, [statusFilter, debouncedSearch, appliedMiplLocationId, appliedDateFrom, appliedDateTo, skipPageResetRef]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!filterOpen) return;
      const el = filterRef.current;
      if (el && !el.contains(e.target)) {
        setFilterOpen(false);
        setFilterSub(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [filterOpen]);

  useEffect(() => {
    if (!filterOpen || filterSub !== "location") return;
    let cancelled = false;
    const loadLoc = async () => {
      setLocationsLoading(true);
      try {
        const res = await handleApiCall("get", endpoint.listOfAlllocation, {});
        if (cancelled) return;
        if (res?.data?.code === 200) {
          const d = res?.data?.data;
          const raw = Array.isArray(d) ? d : d?.location_data ?? d?.locations ?? [];
          const list = Array.isArray(raw) ? raw : [];
          setLocations(
            list.map((x) => ({
              id: x?._id ?? x?.id ?? "",
              name: x?.name ?? x?.location_name ?? "—",
            }))
          );
        } else {
          setLocations([]);
        }
      } catch {
        if (!cancelled) setLocations([]);
      } finally {
        if (!cancelled) setLocationsLoading(false);
      }
    };
    loadLoc();
    return () => {
      cancelled = true;
    };
  }, [filterOpen, filterSub]);
  const searchForApi = (() => {
    const s = clampSearchInput(debouncedSearch).trim();
    if (!s) return '';
    if (/^\+?[\d\s-]+$/.test(s)) return s.replace(/\D/g, '');
    return s;
})();
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          search: searchForApi,
          page: String(currentPage),
          limit: String(itemsPerPage),
        });
        const statusParam = STATUS_OPTIONS.find((t) => t.key === statusFilter)?.status;
        if (statusParam != null && statusParam !== "") {
          params.set("status", statusParam);
        }
        if (appliedMiplLocationId) params.set("mipl_location_id", appliedMiplLocationId);
        appendDateRangeParams(
          params,
          { start: appliedDateFrom, end: appliedDateTo },
          DATE_PARAM_KEYS.startEnd
        );

        const response = await handleApiCall(
          "get",
          `${endpoint.listOfTripHistory}?${params.toString()}`,
          {}
        );
        if (response?.data?.code === 200) {
          const payload = response?.data?.data || {};
          const raw =
            payload.trip_history_data ||
            payload.trip_data ||
            payload.trips ||
            payload.data ||
            [];
          const list = Array.isArray(raw) ? raw : [];
          setRows(list.map(mapTripListingRow));
          setTotal(typeof payload.total === "number" ? payload.total : list.length);
          setTotalPages(Math.max(1, typeof payload.total_pages === "number" ? payload.total_pages : 1));
        } else {
          setRows([]);
          setTotal(0);
          setTotalPages(1);
          // const errMsg =
          //   response?.response?.data?.errorMessage ||
          //   response?.response?.data?.message ||
          //   response?.data?.message ||
          //   "Failed to load trip history";
          // notifyError(errMsg);
          notifyError(getApiErrorMessage(response, 'Failed to load trip history'));
        }
      } catch {
        setRows([]);
        setTotal(0);
        setTotalPages(1);
        notifyError("Failed to load trip history");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [
    statusFilter,
    debouncedSearch,
    currentPage,
    itemsPerPage,
    appliedMiplLocationId,
    appliedDateFrom,
    appliedDateTo,
    listRefreshKey,
  ]);

  const displayFrom = total === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const displayTo = Math.min(currentPage * itemsPerPage, total);
  const hasActiveFilters = Boolean(appliedMiplLocationId || appliedDateFrom || appliedDateTo);
  const hasPartialDateRange = Boolean((dateFrom && !dateTo) || (!dateFrom && dateTo));
  const hasCompleteDateRange = Boolean(dateFrom && dateTo);
  const hasInvalidDateRange = hasCompleteDateRange && dateFrom > dateTo;
  const hasFilterValues = Boolean(
    hasActiveFilters || miplLocationId || dateFrom || dateTo
  );
  const canApplyFilters =
    Boolean(miplLocationId || hasCompleteDateRange) &&
    !hasPartialDateRange &&
    !hasInvalidDateRange;

  const applyFilters = () => {
    setAppliedMiplLocationId(miplLocationId.trim());
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setFilterOpen(false);
    setFilterSub(null);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setMiplLocationId("");
    setDateFrom("");
    setDateTo("");
    setAppliedMiplLocationId("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
    setFilterOpen(false);
    setFilterSub(null);
    setCurrentPage(1);
  };

  const resetCancelFormFields = () => {
    setSelectedCancelTypeId("");
    setCancelReason("");
    setTripAmount("");
    setAwardAmount("0");
    setDebitAmount("0");
    setCancelFieldError({ field: "", message: "" });
  };

  const clearCancelFieldError = (field) => {
    setCancelFieldError((prev) =>
      prev.field === field ? { field: "", message: "" } : prev
    );
  };

  const setCancelFieldErrorFor = (field, message) => {
    setCancelFieldError({ field, message });
  };

  const validateCancelForm = () => {
    if (!selectedCancelTypeId) {
      setCancelFieldErrorFor("cancelType", "Please select the type");
      return false;
    }
    const reasonTrim = cancelReason.trim();
    const reasonError = validateWordCount(reasonTrim, "Reason");
    if (reasonError) {
      setCancelFieldErrorFor("reason", reasonError);
      return false;
    }

    const variant = selectedCancelType?.variant;

    if (variant === "down") {
      const clampedTripAmount = clampTripAmountValue(tripAmount, cancelTrip?.maxTripAmount);
      const trip = Number(clampedTripAmount);
      if (!Number.isFinite(trip) || trip <= 0) {
        setCancelFieldErrorFor("tripAmount", "Please enter a valid trip amount");
        return false;
      }
      if (clampedTripAmount !== tripAmount) setTripAmount(clampedTripAmount);
    }

    if (variant === "technical" || variant === "down") {
      const award = getAwardAmountValue(awardAmount);
      if (!Number.isFinite(award)) {
        setCancelFieldErrorFor("awardAmount", "Please enter a valid award amount");
        return false;
      }
      if (Math.abs(award) > AWARD_AMOUNT_LIMIT) {
        setCancelFieldErrorFor("awardAmount", "Award amount cannot exceed 99999.");
        return false;
      }
    }

    const debit = getDebitNoteAmountValue(debitAmount);
    if (!Number.isFinite(debit) || debit < 0 || debit > DEBIT_NOTE_AMOUNT_MAX) {
      setCancelFieldErrorFor(
        "debitAmount",
        "Debit note amount must be between 0 and 9999"
      );
      return false;
    }

    setCancelFieldError({ field: "", message: "" });
    return true;
  };

  const openCancelModal = (row) => {
    setCancelTrip(row);
    resetCancelFormFields();
    setCancelTypes([]);
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setCancelModalOpen(false);
    setCancelTrip(null);
    resetCancelFormFields();
    setCancelTypes([]);
    setCancelTypesLoading(false);
    setCancelSubmitting(false);
  };

  const handleCancelTypeChange = (typeId) => {
    const nextCancelType = cancelTypes.find((item) => item.id === typeId) ?? null;
    setSelectedCancelTypeId(typeId);
    setCancelReason("");
    setTripAmount("");
    setAwardAmount(
      nextCancelType?.variant === "technical" || nextCancelType?.variant === "down" ? "0" : ""
    );
    setDebitAmount("0");
    clearCancelFieldError("cancelType");
  };

  useEffect(() => {
    if (!cancelModalOpen) return undefined;

    let cancelled = false;
    const fetchCancelTypes = async () => {
      setCancelTypesLoading(true);
      try {
        const response = await handleApiCall("get", endpoint.getTripCancelTypeList, {});
        if (cancelled) return;
        if (response?.data?.code === 200) {
          const d = response?.data?.data ?? {};
          const list = extractCancelTypeList(d).map(mapCancelTypeOption).filter((x) => x.id);
          setCancelTypes(list);
        } else {
          setCancelTypes([]);
          notifyError(getApiErrorMessage(response, "Failed to load cancel types"));
        }
      } catch {
        if (!cancelled) {
          setCancelTypes([]);
          notifyError("Failed to load cancel types");
        }
      } finally {
        if (!cancelled) setCancelTypesLoading(false);
      }
    };

    fetchCancelTypes();
    return () => {
      cancelled = true;
    };
  }, [cancelModalOpen]);

  const handleCancelConfirm = async () => {
    if (!cancelTrip?.id) {
      notifyError("Trip not found");
      return;
    }
    if (!validateCancelForm()) return;

    const variant = selectedCancelType?.variant;
    const payload = {
      trip_id: cancelTrip.id,
      trip_cancel_type_id: selectedCancelTypeId,
      trip_cancel_reason: cancelReason.trim(),
    };

    if (variant === "technical" || variant === "down") {
      payload.award_amount = getAwardAmountValue(awardAmount);
    }

    if (variant === "down") {
      payload.trip_amount = Number(clampTripAmountValue(tripAmount, cancelTrip?.maxTripAmount));
    }

    payload.debit_note_amount = getDebitNoteAmountValue(debitAmount);

    setCancelSubmitting(true);
    try {
      const response = await handleApiCall("post", endpoint.cancelTrip, payload);
      if (isCancelTripSuccessResponse(response)) {
        notifySuccess(getCancelTripResponseMessage(response, "Trip cancelled successfully"));
        closeCancelModal();
        handleStatusFilterChange("cancelled");
        setListRefreshKey((key) => key + 1);
        return;
      }
      notifyError(
        getCancelTripResponseMessage(response) ||
          getApiErrorMessage(response, "Failed to cancel trip")
      );
    } catch {
      notifyError("Failed to cancel trip");
    } finally {
      setCancelSubmitting(false);
    }
  };

  const isOngoingFilter = statusFilter === "ongoing";
  const isCancelledFilter = statusFilter === "cancelled";
  const tableColSpan = isCancelledFilter ? 15 : 10;

  const showCancelTripAction = (row) =>
    Boolean(row.id && (isOngoingFilter || (!statusFilter && row.status === "Ongoing")));

  return (
    <>
      <div className="md:mt-7 mt-3 font-UbuntuRegular">
        {/* <h2 className="mb-4 text-xl font-UbuntuMedium text-[#030253]">Trip History</h2> */}

        {/* Search, status filter, and filters — one row */}
        <div className="mb-2 md:!mb-5 flex flex-wrap items-center md:gap-3 gap-2">
          <input
            type="search"
            placeholder="Search by company, location, vehicle no. or challan no."
            value={search}
            maxLength={SEARCH_MAX_LENGTH}
            onChange={(e) => setSearch(clampSearchInput(e.target.value))}
            className="searchIcon h-10 min-w-[12rem] flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-800 shadow-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary sm:max-w-md lg:max-w-lg"
          />
          <select
            id="trip-history-status"
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="h-10 w-full max-[767px]:max-w-[calc(100%-3.5rem)] min-w-[11rem] shrink-0 rounded-md border border-gray-300 bg-white px-3 text-sm font-UbuntuMedium text-gray-800 shadow-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary sm:w-auto"
          >
            {STATUS_OPTIONS.map(({ key, label }) => (
              <option key={key || "all"} value={key}>
                {label}
              </option>
            ))}
          </select>
          <div className="relative shrink-0" ref={filterRef}>
            <button
              type="button"
              onClick={() => {
                setFilterOpen((o) => !o);
                setFilterSub(null);
              }}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50"
              aria-label="Filters"
            >
              <img src={FilterIcon} alt="" className="h-5 w-5 invert grayscale brightness-100" />
            </button>
            {filterOpen && (
              <div className="absolute left-0 z-50 mt-2 min-w-[220px] rounded-lg border border-primary/25 bg-white py-2 shadow-lg sm:left-auto max-[767px]:right-0 max-[767px]:left-auto sm:right-0">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-4 py-2 text-left text-sm text-[#030253] hover:bg-primary/5"
                  onClick={() => setFilterSub(filterSub === "location" ? null : "location")}
                >
                  MIPL Location
                  <FilterChevron open={filterSub === "location"} />
                </button>
                {filterSub === "location" && (
                  <div className="border-t border-gray-100 px-4 py-3">
                    {locationsLoading ? (
                      <p className="mb-0 text-xs text-gray-500">Loading…</p>
                    ) : (
                      <select
                        className="w-full rounded border border-primary/30 p-2 text-sm outline-none focus:border-primary"
                        value={miplLocationId}
                        onChange={(e) => setMiplLocationId(e.target.value)}
                      >
                        <option value="">All locations</option>
                        {locations.map((loc) => (
                          <option key={loc.id || loc.name} value={loc.id}>
                            {loc.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="flex w-full items-center justify-between border-t border-gray-100 px-4 py-2 text-left text-sm text-[#030253] hover:bg-primary/5"
                  onClick={() => setFilterSub(filterSub === "dates" ? null : "dates")}
                >
                  Date Range
                  <FilterChevron open={filterSub === "dates"} />
                </button>
                {filterSub === "dates" && (
                  <div className="border-t border-gray-100 px-4 py-3">
                    <DateRangeFilter
                      variant="embedded"
                      from={dateFrom}
                      to={dateTo}
                      onChange={({ from, to }) => {
                        setDateFrom(from);
                        setDateTo(to);
                      }}
                    />
                  </div>
                )}
                <div className="flex gap-2 border-t border-gray-100 px-4 py-2">
                  {hasFilterValues && (
                    <button
                      type="button"
                      className="btn w-full !rounded-lg !border-gray-300 !bg-white !py-2 text-sm !text-gray-800 hover:!bg-gray-50"
                      onClick={clearFilters}
                    >
                      Clear
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary w-full !rounded-lg !py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!canApplyFilters}
                    onClick={applyFilters}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 sm:ml-auto max-[767px]:w-full max-[767px]:justify-center">
            <DateRangeAppliedLabel
              from={appliedDateFrom}
              to={appliedDateTo}
              prefix="Selected Date Range: "
              className="my-0"
            />
            <span className="text-sm text-gray-600">({total} Trips)</span>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="rounded-lg border border-gray-300 bg-white px-3 md:px-4 py-2 text-xs md:text-sm font-UbuntuMedium text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        <div className="common-table-wrapper m-0 shadow-sm">
          <div className="table-responsive">
            <table className="w-full text-sm">
              <thead className="bg-[#030253] text-white [&_th]:font-UbuntuMedium [&_th]:px-3 [&_th]:py-3">
                <tr>
                  <th>S.No.</th>
                  <th>Trip ID</th>
                  <th>Company Name</th>
                  <th>Location</th>
                  <th>MIPL Location</th>
                  <th>Vehicle No.</th>
                  <th>Challan No.</th>
                  <th>Meter</th>
                  {isCancelledFilter ? (
                    <>
                      <th>Cancelled At</th>
                      <th>Cancel Type</th>
                      <th>Trip Amount</th>
                      <th>Award Amount</th>
                      <th>Reason</th>
                      <th>Cancelled By</th>
                    </>
                  ) : (
                    <th>Status</th>
                  )}
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={tableColSpan} className="py-8 text-center text-gray-500">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading &&
                  rows.map((row, index) => (
                    <tr
                      key={row.id || `r-${index}`}
                      className={`border-t border-gray-100 text-center ${
                        index % 2 === 0 ? "bg-white" : "bg-[primary]/[0.04]"
                      }`}
                    >
                      <td className="py-2.5">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td className="py-2.5 whitespace-nowrap" title={row.tripRefId}>
                        {row.tripRefId}
                      </td>
                      <td className="py-2.5 text-left pl-3" title={row.company}>
                        {trimText(row.company, 24)}
                      </td>
                      <td className="py-2.5" title={row.location}>
                        {trimText(row.location, 18)}
                      </td>
                      <td className="py-2.5" title={row.miplLocation}>
                        {trimText(row.miplLocation, 18)}
                      </td>
                      <td className="py-2.5" title={row.vehicle}>
                        {trimText(row.vehicle, 18)}
                      </td>
                      <td className="py-2.5" title={row.challanNumber}>
                        {trimText(row.challanNumber, 18)}
                      </td>
                      <td className="py-2.5 whitespace-nowrap">
                        <TripMeterStatusBadge stopped={row.meterStopped} />
                      </td>
                      {isCancelledFilter ? (
                        <>
                          <td className="py-2.5 whitespace-nowrap" title={row.cancelledAt}>
                            {trimText(row.cancelledAt, 22)}
                          </td>
                          <td className="py-2.5" title={row.cancelType}>
                            {trimText(row.cancelType, 18)}
                          </td>
                          <td className="py-2.5 whitespace-nowrap" title={row.cancelTripAmount}>
                            {row.cancelTripAmount}
                          </td>
                          <td className="py-2.5 whitespace-nowrap" title={row.awardAmount}>
                            {row.awardAmount}
                          </td>
                          <td className="py-2.5 text-left pl-3" title={row.cancelReason}>
                            {trimText(row.cancelReason, 24)}
                          </td>
                          <td className="py-2.5" title={row.cancelledBy}>
                            {trimText(row.cancelledBy, 18)}
                          </td>
                        </>
                      ) : (
                        <td className="py-2.5">
                          <span
                            className={
                              row.status === "Ongoing"
                                ? "font-UbuntuMedium text-secondary"
                                : "font-UbuntuMedium text-[#030253]"
                            }
                          >
                            {row.status}
                          </span>
                        </td>
                      )}
                      <td className="py-2.5">
                        <div className="inline-flex items-center justify-center gap-2">
                          {row.id ? (
                            <PermissionGate module={MODULES.TRIP_HISTORY} action="view">
                              <Link
                                to={`/trip-history/view-trip-history-details?trip_id=${encodeURIComponent(row.id)}${detailListQuery}`}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-primary/25 bg-primary/5 hover:bg-primary/10"
                                aria-label="View trip"
                              >
                                <img src={EyeIcon} alt="" className="h-5 w-5" />
                              </Link>
                            </PermissionGate>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                          {showCancelTripAction(row) ? (
                            <PermissionGate module={MODULES.TRIP_HISTORY} action="edit">
                              <button
                                type="button"
                                onClick={() => openCancelModal(row)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-200 bg-red-50 hover:bg-red-100"
                                aria-label="Cancel trip"
                              >
                                <img src={CancelIcon} alt="" className="h-5 w-5" />
                              </button>
                            </PermissionGate>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={tableColSpan} className="py-8 text-center text-gray-500">
                      No trip history available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-2 md:gap-x-4 md:pr-4">
          <select
            className="rounded border border-primary/30 p-2 text-sm shadow-sm outline-none focus:border-primary"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
          <div className="text-xs md:text-sm text-gray-500 ml-auto max-[767px]:max-w-[80px]">
            {loading
              ? "—"
              : total === 0
                ? "0 records found"
                : `${displayFrom}–${displayTo} of ${total} records found`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1 || loading || total === 0}
              className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center "
            >
              <img src={LeftAngle} className="invert brightness-100 grayscale" alt="" />
            </button>
            <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
            <span className="text-sm text-gray-600">of {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages || loading || total === 0}
              className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center "
            >
              <img src={RightAngle} className="invert brightness-100 grayscale" alt="" />
            </button>
          </div>
        </div>
      </div>

      {cancelModalOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-trip-title"
        >
          <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-lg ring-2 ring-primary/20">
            <div className="bg-[#030253] px-6 py-2.5">
              <h2
                id="cancel-trip-title"
                className="mb-0 text-center font-UbuntuBold !text-base !text-white"
              >
                Cancel Trip
              </h2>
            </div>
            <div className="space-y-2.5 px-6 pb-4 pt-3 font-UbuntuRegular">
              {cancelTrip ? (
                <div className="text-center text-sm leading-snug text-gray-600">
                  <p className="mb-0">
                    Trip ID:{" "}
                    <span className="font-UbuntuBold text-gray-800">{cancelTrip.tripRefId}</span>
                  </p>
                  <p className="mb-0">
                    Challan No.:{" "}
                    <span className="font-UbuntuBold text-gray-800">{cancelTrip.challanNumber}</span>
                  </p>
                </div>
              ) : null}
              <div>
              <label
                htmlFor="cancel-trip-type"
                className="mb-1 block text-sm font-UbuntuMedium text-gray-800"
              >
                Cancel Type
              </label>
              {cancelTypesLoading ? (
                <p className="mb-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-500">
                  Loading cancel types…
                </p>
              ) : (
                <select
                  id="cancel-trip-type"
                  value={selectedCancelTypeId}
                  onChange={(e) => handleCancelTypeChange(e.target.value)}
                  className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                >
                  <option value="">
                    {cancelTypes.length ? "Select cancel type" : "No cancel types available"}
                  </option>
                  {cancelTypes.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
              {cancelFieldError.field === "cancelType" ? (
                <p className="mb-0 mt-1 text-xs text-red-500">{cancelFieldError.message}</p>
              ) : null}
              </div>

              <div>
                <label
                  htmlFor="cancel-trip-reason"
                  className="mb-1 block text-sm font-UbuntuMedium text-gray-800"
                >
                  Reason
                </label>
                <textarea
                  id="cancel-trip-reason"
                  rows={2}
                  value={cancelReason}
                  onChange={(e) => {
                    setCancelReason(limitTextInput(e.target.value));
                    clearCancelFieldError("reason");
                  }}
                  placeholder={
                    selectedCancelTypeId ? "Enter reason" : "Select cancel type first"
                  }
                  disabled={!selectedCancelTypeId}
                  className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
                />
                <p className="mb-0 mt-1 text-xs text-gray-500">{WORD_COUNT_HINT}</p>
                {cancelFieldError.field === "reason" ? (
                  <p className="mb-0 mt-1 text-xs text-red-500">{cancelFieldError.message}</p>
                ) : null}
              </div>

              {selectedCancelTypeId ? (
                <>
                  {selectedCancelType?.variant === "down" ? (
                    <div>
                      <label
                        htmlFor="cancel-trip-amount"
                        className="mb-1 block text-sm font-UbuntuMedium text-gray-800"
                      >
                        Trip Amount (₹)
                      </label>
                      <input
                        id="cancel-trip-amount"
                        type="number"
                        min="0"
                        max={cancelTrip?.maxTripAmount ?? undefined}
                        step="0.01"
                        value={tripAmount}
                        onChange={(e) => {
                          setTripAmount(
                            clampTripAmountValue(e.target.value, cancelTrip?.maxTripAmount)
                          );
                          clearCancelFieldError("tripAmount");
                        }}
                        placeholder="Enter trip amount"
                        className={amountFieldClassName}
                      />
                      {cancelFieldError.field === "tripAmount" ? (
                        <p className="mb-0 mt-1 text-xs text-red-500">{cancelFieldError.message}</p>
                      ) : cancelTrip?.maxTripAmount != null ? (
                        <p className="mb-0 mt-1 text-xs text-gray-400">
                          Max limit: {formatTripAmount(cancelTrip.maxTripAmount)}
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {selectedCancelType?.variant === "down" ||
                  selectedCancelType?.variant === "technical" ? (
                    <div>
                      <label
                        htmlFor="cancel-award-amount"
                        className="mb-1 block text-sm font-UbuntuMedium text-gray-800"
                      >
                        Award Amount (₹)
                      </label>
                      <input
                        id="cancel-award-amount"
                        type="text"
                        inputMode="decimal"
                        value={awardAmount}
                        onChange={(e) => {
                          setAwardAmount(normalizeAwardAmountInput(e.target.value));
                          clearCancelFieldError("awardAmount");
                        }}
                        placeholder="Enter award amount"
                        className={amountFieldClassName}
                      />
                      {cancelFieldError.field === "awardAmount" ? (
                        <p className="mb-0 mt-1 text-xs text-red-500">{cancelFieldError.message}</p>
                      ) : null}
                    </div>
                  ) : null}

                  <div>
                    <label
                      htmlFor="cancel-debit-amount"
                      className="mb-1 block text-sm font-UbuntuMedium text-gray-800"
                    >
                      Debit Note Amount (₹)
                    </label>
                    <input
                      id="cancel-debit-amount"
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      value={debitAmount}
                      onChange={(e) => {
                        setDebitAmount(normalizeDebitNoteAmountInput(e.target.value));
                        clearCancelFieldError("debitAmount");
                      }}
                      placeholder="0"
                      className={amountFieldClassName}
                    />
                    {cancelFieldError.field === "debitAmount" ? (
                      <p className="mb-0 mt-1 text-xs text-red-500">{cancelFieldError.message}</p>
                    ) : null}
                  </div>
                </>
              ) : null}

              <div className="flex items-center justify-center gap-3 pt-1">
                <button
                  type="button"
                  className="inline-flex h-11 min-w-[8rem] items-center justify-center rounded-full border border-[#030253] bg-white px-8 text-sm font-UbuntuMedium text-[#030253] transition-colors hover:bg-gray-50 disabled:opacity-50"
                  onClick={closeCancelModal}
                  disabled={cancelSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="inline-flex h-11 min-w-[8rem] items-center justify-center rounded-full border border-[#030253] bg-[#030253] px-8 text-sm font-UbuntuMedium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                  onClick={handleCancelConfirm}
                  disabled={cancelSubmitting || cancelTypesLoading}
                >
                  {cancelSubmitting ? "Submitting…" : "Yes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <Toastify />
    </>
  );
};

export default TripHistoryListing;
